<script setup lang="ts">
// The menu panel: portalled to <body> and positioned by Reka's Floating-UI
// (collision-aware flip), so `side`/`align` replace the apps' hand-rolled
// getBoundingClientRect math. Arbitrary markup is allowed in the default slot
// (a head row, separators); wrap actionable rows in <MenuItem> for keyboard
// nav. Tokens resolve off :root, so the same panel themes to either app.
import { DropdownMenuPortal, DropdownMenuContent } from 'reka-ui';

withDefaults(defineProps<{
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;
  collisionPadding?: number;
  /** aria-label when the menu has no visible labelling element. */
  label?: string | undefined;
  /** Floor for the panel width (the trigger width is not inherited). */
  minWidth?: string | undefined;
}>(), {
  side: 'bottom',
  align: 'start',
  sideOffset: 6,
  alignOffset: 0,
  collisionPadding: 8,
});
</script>

<template>
  <DropdownMenuPortal>
    <DropdownMenuContent
      class="hq-menu"
      :side="side"
      :align="align"
      :side-offset="sideOffset"
      :align-offset="alignOffset"
      :collision-padding="collisionPadding"
      :aria-label="label"
      :style="minWidth ? { minWidth } : undefined"
    >
      <slot />
    </DropdownMenuContent>
  </DropdownMenuPortal>
</template>

<!-- Unscoped: the content is portalled to <body> by Reka, so a scoped style
     hash would never reach it (same reason AppNav's teleported menu is global).
     Classes are `hq-menu`-prefixed to avoid collisions. -->
<style>
.hq-menu {
  z-index: 9000;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: min(360px, calc(100vw - 16px));
  padding: var(--space-2);
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-overlay);
  /* Reka writes the transform-origin for the chosen side; animate off it. */
  transform-origin: var(--reka-dropdown-menu-content-transform-origin);
}
.hq-menu[data-state="open"] { animation: hq-menu-in var(--dur-1, 0.12s) var(--ease-out, ease); }
.hq-menu:focus-visible { outline: none; }

@keyframes hq-menu-in {
  from { opacity: 0; transform: scale(0.97); }
}
@media (prefers-reduced-motion: reduce) {
  .hq-menu[data-state="open"] { animation: none; }
}
</style>
